<template>
  <section class="mt-4 mb-5">
    <div class="container-fluid"> 

      <div class="row justify-content-center">
        <div class="col-lg-5 col-md-7">
          <div class="sticky"> 
            <!-- Page header -->
            <div class="page-bar py-3 mb-4">
              <div class="container">
                <div class="d-flex align-items-center">
                  <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                    <i class="fas fa-chevron-left"></i>
                  </BackButton>
                  <div class="page-title flex-grow-1 text-center">Change Password</div>
                </div>
              </div>
            </div>

            <form @submit.prevent="handleSubmit" id="passwordForm" class="password-card">
              <div class="card shadow-sm border-0">
                <div class="card-body">
                  <p class="text-muted small mb-4">
                    Keep your account secure by choosing a password with at least 10 characters,
                    mixed case letters, numbers, and special symbols.
                  </p>

                  <!-- Old password -->
                  <div class="form-group mb-4">
                    <label class="form-control-label">Old Password</label>
                    <div class="password-input">
                      <input
                        :type="showOld ? 'text' : 'password'"
                        v-model.trim="form.oldpwd"
                        class="form-control"
                        placeholder="Enter old password"
                        autocomplete="current-password"
                      />
                      <button
                        type="button"
                        class="input-action"
                        @click="showOld = !showOld"
                        :aria-label="showOld ? 'Hide password' : 'Show password'"
                      >
                        <i :class="['fas', showOld ? 'fa-eye-slash' : 'fa-eye']"></i>
                      </button>
                    </div>
                  </div>

                  <!-- New password -->
                  <div class="form-group mb-4">
                    <label class="form-control-label d-flex justify-content-between align-items-center">
                      <span>New Password</span>
                      <button type="button" class="link-btn" @click="handleGeneratePassword">
                        <i class="fas fa-magic mr-1"></i>Generate
                      </button>
                    </label>
                    <div class="password-input">
                      <input
                        :type="showNew ? 'text' : 'password'"
                        v-model.trim="form.newpwd"
                        class="form-control"
                        placeholder="Enter new password"
                        autocomplete="new-password"
                      />
                      <button
                        type="button"
                        class="input-action"
                        @click="showNew = !showNew"
                        :aria-label="showNew ? 'Hide password' : 'Show password'"
                      >
                        <i :class="['fas', showNew ? 'fa-eye-slash' : 'fa-eye']"></i>
                      </button>
                    </div>

                    <div class="strength-meter mt-2" aria-hidden="true">
                      <div
                        class="strength-meter__bar"
                        :class="passwordStrengthClass"
                        :style="{ width: passwordStrengthPercent + '%' }"
                      ></div>
                    </div>
                    <div class="strength-label">{{ passwordStrengthLabel }}</div>

                    <ul class="password-rules mt-3">
                      <li :class="{ passed: passwordRules.hasUpper }">At least one uppercase letter</li>
                      <li :class="{ passed: passwordRules.hasLower }">At least one lowercase letter</li>
                      <li :class="{ passed: passwordRules.hasNumber }">At least one number</li>
                      <li :class="{ passed: passwordRules.hasSpecial }">At least one special character</li>
                      <li :class="{ passed: passwordRules.hasLength }">Minimum 10 characters</li>
                    </ul>
                  </div>

                  <!-- Confirm password -->
                  <div class="form-group mb-2">
                    <label class="form-control-label">Confirm Password</label>
                    <div class="password-input">
                      <input
                        :type="showConfirm ? 'text' : 'password'"
                        v-model.trim="form.confirm"
                        class="form-control"
                        placeholder="Re-enter new password"
                        autocomplete="new-password"
                      />
                      <button
                        type="button"
                        class="input-action"
                        @click="showConfirm = !showConfirm"
                        :aria-label="showConfirm ? 'Hide password' : 'Show password'"
                      >
                        <i :class="['fas', showConfirm ? 'fa-eye-slash' : 'fa-eye']"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-transparent text-center border-0">
                  <button type="submit" class="btn upload-btn w-50" :disabled="isSubmitDisabled">
                    {{ loading ? "Please wait…" : "Submit" }}
                  </button>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed } from "vue";
import Swal from "sweetalert2";
import { useHead } from "@vueuse/head";
import { useSessionStore } from "@/stores/session";
import api from "@/services/api";
import BackButton from '@/components/common/BackButton.vue'

useHead({
  title: "Change Password - Netflyr",
  meta: [
    { name: "description", content: "Change your Netflyr account password securely." },
    { name: "keywords", content: "change password, security, netflyr, account settings" },
  ],
});

const session = useSessionStore();
const userId = ref(session.userId || null);

const form = reactive({ oldpwd: "", newpwd: "", confirm: "" });
const loading = ref(false);

// visibility toggles
const showOld = ref(false);
const showNew = ref(false);
const showConfirm = ref(false);

const toast = (icon, title, timer = 2500) =>
  Swal.fire({ toast: true, position: "top-end", showConfirmButton: false, timer, icon, title });

const passwordRules = computed(() => {
  const pwd = form.newpwd || "";
  return {
    hasUpper: /[A-Z]/.test(pwd),
    hasLower: /[a-z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
    hasSpecial: /[^A-Za-z0-9]/.test(pwd),
    hasLength: pwd.length >= 10,
  };
});

const passwordStrength = computed(() =>
  Object.values(passwordRules.value).filter(Boolean).length
);

const passwordStrengthPercent = computed(() => (passwordStrength.value / 5) * 100);

const passwordStrengthLabel = computed(() => {
  if (!form.newpwd) return "Strength: Empty";
  if (passwordStrength.value <= 2) return "Strength: Weak";
  if (passwordStrength.value <= 4) return "Strength: Medium";
  return "Strength: Strong";
});

const passwordStrengthClass = computed(() => {
  if (passwordStrength.value <= 2) return "weak";
  if (passwordStrength.value <= 4) return "medium";
  return "strong";
});

const isSubmitDisabled = computed(() => {
  if (loading.value) return true;
  return !form.oldpwd || !form.newpwd || !form.confirm;
});

const generateSecurePassword = () => {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "!@#$%^&*()_-+=<>?";
  const allChars = uppercase + lowercase + numbers + special;

  const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
  const result = [
    pick(uppercase),
    pick(lowercase),
    pick(numbers),
    pick(special),
  ];

  while (result.length < 12) {
    result.push(pick(allChars));
  }

  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result.join("");
};

const handleGeneratePassword = () => {
  const pwd = generateSecurePassword();
  form.newpwd = pwd;
  form.confirm = pwd;
};

const handleSubmit = async () => {
  if (!form.oldpwd || !form.newpwd || !form.confirm) {
    return toast("warning", "All fields are required");
  }
  if (form.newpwd !== form.confirm) {
    return toast("error", "New passwords do not match");
  }
  if (Object.values(passwordRules.value).includes(false)) {
    return toast("warning", "Password must meet all requirements");
  }
  loading.value = true;
  try {
    const fd = new FormData();
    fd.append("id", userId.value);
    fd.append("oldpwd", form.oldpwd);
    fd.append("newpwd", form.newpwd);

    const res = await api.changePassword(fd);
    if (res?.data?.status === "ok") {
      toast("success", "Password changed successfully!");
      form.oldpwd = "";
      form.newpwd = "";
      form.confirm = "";
      showOld.value = showNew.value = showConfirm.value = false;
    } else {
      toast("error", res?.data?.message || "Old password incorrect");
    }
  } catch (e) {
    console.error(e);
    toast("error", "Something went wrong");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.password-card .card {
  border-radius: 20px;
}

.password-input {
  position: relative;
}

.password-input .form-control {
  padding-right: 48px;
  border-radius: 14px;
}

.input-action {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #475569;
  font-size: 1rem;
  cursor: pointer;
}

.link-btn {
  border: none;
  background: transparent;
  color: #FB7210;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.strength-meter {
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}
.strength-meter__bar {
  height: 100%;
  width: 0;
  border-radius: inherit;
  transition: width 0.3s ease;
}
.strength-meter__bar.weak { background: #dc3545; }
.strength-meter__bar.medium { background: #f0ad4e; }
.strength-meter__bar.strong { background: #28a745; }
.strength-label {
  font-size: 0.85rem;
  color: #475569;
  margin-top: 6px;
}

.password-rules {
  list-style: none;
  padding-left: 0;
  margin: 0;
  color: #94a3b8;
  font-size: 0.85rem;
}
.password-rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.password-rules li::before {
  content: "•";
  color: #FB7210;
}
.password-rules li.passed {
  color: #28a745;
}
.password-rules li.passed::before {
  content: "✓";
}

.upload-btn {
  background-color: #FB7210;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: 0.3s;
}
.upload-btn:hover { background-color: #084cb3; }
</style>
